import { useDispatch } from 'react-redux';
import { selectOffice } from '~/actions';
import { ConfirmCandidateAnotherJobPopup } from '~/components/ConfirmCandidate/components/ConfirmCandidateAnotherJobPopup';
import { ConfirmCandidateEligibleJobsPopup } from '~/components/ConfirmCandidate/components/ConfirmCandidateEligibleJobsPopup';
import { DeclineSuccessPopups } from '~/components/ConfirmCandidate/components/DeclineSuccessPopups';
import { PaymentMissingPopup } from '~/components/ConfirmCandidate/components/PaymentMissingPopup';
import { IJob } from '~/models';
import type { IAnotherJobPrompt, IEligibleJobsPrompt } from '../hooks/useConfirmCandidateActions';

export interface IConfirmOutcomePopupsProps {
  job: Partial<IJob> | null | undefined;
  /** The `outcome` slice of `useConfirmCandidateActions`. */
  anotherJobPrompt: IAnotherJobPrompt | null;
  eligibleJobsPrompt: IEligibleJobsPrompt | null;
  isPaymentMissing: boolean;
  isDeclineSuccess: boolean;
  isEnablePermanentJobM2M: boolean;
  declinedName: string;
  dismissAnotherJob: () => void;
  dismissEligibleJobs: () => void;
  dismissPaymentMissing: () => void;
  closeDeclineSuccess: () => void;
  /** Lifts the navigation guard for an intentional navigation. */
  allowNavigation: () => void;
  /** Escape hatch the eligible-jobs popup uses to navigate past the guard. */
  navigateWithoutBlocking: (path: string) => void;
}

/**
 * What the office sees after a confirm or a decline resolves.
 *
 * All four popups are the legacy screen's, reused by import — the redesign
 * changes which page they open from, not what they say. Rendering them together
 * keeps the page from having to know that a confirm has four possible epilogues.
 */
export const ConfirmOutcomePopups = ({
  job,
  anotherJobPrompt,
  eligibleJobsPrompt,
  isPaymentMissing,
  isDeclineSuccess,
  isEnablePermanentJobM2M,
  declinedName,
  dismissAnotherJob,
  dismissEligibleJobs,
  dismissPaymentMissing,
  closeDeclineSuccess,
  allowNavigation,
  navigateWithoutBlocking,
}: IConfirmOutcomePopupsProps) => {
  const dispatch = useDispatch();

  return (
    <>
      {anotherJobPrompt ? (
        <ConfirmCandidateAnotherJobPopup
          show
          candidate={anotherJobPrompt.candidate}
          officeId={anotherJobPrompt.officeId}
          job={job}
          shiftId={anotherJobPrompt.shiftId}
          offerId={anotherJobPrompt.offerId}
          hasPendingJobs={anotherJobPrompt.hasPendingJobs}
          onClose={dismissAnotherJob}
        />
      ) : null}

      {eligibleJobsPrompt ? (
        <ConfirmCandidateEligibleJobsPopup
          show
          candidate={eligibleJobsPrompt.candidate ?? null}
          professionalId={eligibleJobsPrompt.candidate?.id ?? null}
          officeId={eligibleJobsPrompt.officeId ?? null}
          job={eligibleJobsPrompt.job ?? null}
          onClose={dismissEligibleJobs}
          navigateWithoutBlocking={navigateWithoutBlocking}
        />
      ) : null}

      {isPaymentMissing ? (
        <PaymentMissingPopup
          onGoBack={dismissPaymentMissing}
          onAddPayment={() => {
            allowNavigation();
            dismissPaymentMissing();
            dispatch(
              selectOffice({ officeId: job?.offer_owner?.id, redirectUrl: '/account/payment' }),
            );
          }}
        />
      ) : null}

      {isDeclineSuccess ? (
        <DeclineSuccessPopups
          isEnablePermanentJobM2M={isEnablePermanentJobM2M}
          professionalName={declinedName}
          onClose={closeDeclineSuccess}
        />
      ) : null}
    </>
  );
};
